import type { MonitorState } from '@netdeck/contracts';
import type { TFunction } from 'i18next';
import type { Status } from '../../shared/ui/StatusDot';

/**
 * Сводное состояние узла по состояниям его мониторов.
 *
 * Правило §11, фаза 4, задача 2 дословно: «зелёный `up`, красный `down`,
 * серый `unknown`, жёлтый — есть мониторы в разных состояниях». Отсюда
 * и реализация: расходятся — жёлтый, сошлись — их общее состояние.
 *
 * Жёлтый означает не «что-то среднее», а «мониторы разошлись»: узел
 * отвечает на ping, но не отвечает на 445 порту. Свести это к зелёному
 * или красному значит потерять ровно ту информацию, ради которой на узле
 * заведено несколько проверок.
 *
 * Живёт отдельным модулем по двум причинам. Во-первых, правило одно
 * на карту и на список узлов, а два экрана, показывающие один узел
 * по-разному, — это не мелочь, а повод не верить обоим. Во-вторых, модуль
 * рядом с компонентом ломает горячую замену: Vite не может обновить файл,
 * который экспортирует и компоненты, и обычные значения, и перезагружает
 * его целиком — на глаз это секундное замирание оболочки.
 */
export function combineStatuses(states: readonly MonitorState[]): Status {
  if (states.length === 0) {
    return 'unknown';
  }

  const distinct = new Set(states.map((state) => state.status));

  if (distinct.size > 1) {
    return 'warn';
  }

  const [only] = [...distinct];

  return only === 'up' || only === 'down' ? only : 'unknown';
}

/**
 * Состояния мониторов по узлам.
 *
 * Служебные мониторы дополнительных адресов пропускаются (ADR 036): узел
 * с пулом из двухсот адресов, половина которых свободна, иначе был бы вечно
 * жёлтым — и жёлтый перестал бы что-либо значить. Состояние адресов
 * показывается своей полосой точек, а не цветом карточки.
 *
 * Живёт здесь, а не рядом с картой: правило одно на карту и на список узлов.
 */
export function statesByNode(live: Map<string, MonitorState>): Map<string, MonitorState[]> {
  const byNode = new Map<string, MonitorState[]>();

  for (const state of live.values()) {
    if (state.addressId !== null) {
      continue;
    }

    const list = byNode.get(state.nodeId) ?? [];
    list.push(state);
    byNode.set(state.nodeId, list);
  }

  return byNode;
}

/**
 * Состояния служебных мониторов по идентификатору адреса.
 *
 * Ключ — адрес, а не монитор: точку на карте рисует адрес, и знать про
 * монитор ей нужно ровно затем, чтобы найти своё состояние здесь.
 */
export function statesByAddress(live: Map<string, MonitorState>): Map<string, MonitorState> {
  const byAddress = new Map<string, MonitorState>();

  for (const state of live.values()) {
    if (state.addressId !== null) {
      byAddress.set(state.addressId, state);
    }
  }

  return byAddress;
}

export function monitorsSummary(count: number, status: Status, t: TFunction): string {
  const monitors = t('nodes.monitorsCount', { count });

  if (status === 'warn') {
    return t('nodes.summary.warn', { monitors });
  }
  if (status === 'down') {
    return t('nodes.summary.down', { monitors });
  }
  if (status === 'unknown') {
    return t('nodes.summary.unknown', { monitors });
  }
  return monitors;
}
